/**
 * useThemesV2 - Hook for fetching and managing V2 theme list
 *
 * Provides pagination, loading states, and refresh functionality.
 * Uses ThemeV2Service (themesV2/ collection).
 */

import { useState, useEffect, useCallback } from 'react';
import { ThemeV2Service } from '@modules/firebase-core/services/ThemeV2Service';
import type { ThemeV2 } from '../types/themesV2.types';
import { compareThemesV2ByOrder } from '@modules/themes/utils/themeListOrder';

export interface UseThemesV2Options {
  /** Number of themes to fetch per page */
  pageSize?: number;
}

export interface UseThemesV2Return {
  /** List of V2 themes */
  themes: ThemeV2[];
  /** Whether themes are being loaded */
  loading: boolean;
  /** Error if fetch failed */
  error: Error | null;
  /** Whether there are more themes to load */
  hasMore: boolean;
  /** Load more themes */
  loadMore: () => void;
  /** Refresh the theme list */
  refresh: () => void;
}

/**
 * useThemesV2 - Fetch and manage V2 theme list
 */
export function useThemesV2(options?: UseThemesV2Options): UseThemesV2Return {
  const { pageSize = 20 } = options || {};

  const [themes, setThemes] = useState<ThemeV2[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const themeService = new ThemeV2Service();

  const fetchThemes = useCallback(
    async (isRefresh = false) => {
      try {
        setLoading(true);
        setError(null);

        const startAfter = isRefresh ? undefined : cursor;
        const fetchedThemes = await themeService.list({
          limit: pageSize,
          startAfter,
        });

        // Sort by order (ascending), then by createdAt (descending)
        const sortedThemes = [...fetchedThemes].sort(compareThemesV2ByOrder);

        if (isRefresh) {
          setThemes(sortedThemes);
        } else if (startAfter) {
          setThemes((prev) => {
            const combined = [...prev, ...sortedThemes];
            return combined.sort(compareThemesV2ByOrder);
          });
        } else {
          setThemes(sortedThemes);
        }

        setHasMore(fetchedThemes.length >= pageSize);

        if (fetchedThemes.length > 0) {
          const lastTheme = fetchedThemes[fetchedThemes.length - 1];
          setCursor(lastTheme.updatedAt);
        }
      } catch (err) {
        setError(err instanceof Error ? err : new Error('Failed to fetch V2 themes'));
      } finally {
        setLoading(false);
      }
    },
    [cursor, pageSize]
  );

  useEffect(() => {
    fetchThemes(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      fetchThemes(false);
    }
  }, [loading, hasMore, fetchThemes]);

  const refresh = useCallback(() => {
    setCursor(undefined);
    fetchThemes(true);
  }, [fetchThemes]);

  return {
    themes,
    loading,
    error,
    hasMore,
    loadMore,
    refresh,
  };
}

export default useThemesV2;
